import { Link } from '@tanstack/react-router';
import { StatusBadge } from '../ui/Badge';
import { dueChip, dueTitle } from './assetVocab';
import type { DueRow } from './MaintenanceDueTable';

/**
 * One of the fleet overview's two due cards (112, approved prototype D2): the few things
 * coming up soonest, a count, and a way to the whole list.
 *
 * ⚠️ NOT `MaintenanceDueTable` with a smaller limit. That component is a full client-mode
 * `DataTable` — sortable headers, per-column filters, a pager, a "Mark done" button per
 * row and mount-scoped state for all of it. Putting one in a dashboard card would put two
 * independent sort/filter/page states on a screen whose whole job is a glance, and 103
 * already found what happens when two DataTables share a surface. This is a list. The
 * table is one click away and this card is the click.
 *
 * The two cards differ only in their rows and their words, so they are ONE component
 * called twice — `card` on the row already says which side of the partition a thing is on
 * (`MAINTENANCE_CARD_TYPES` / `REMINDER_CARD_TYPES`, settled in shared since 106), and a
 * second component would be a second place for that split to drift.
 */
export function DueCard({
  title,
  rows,
  total,
  viewAllTab,
  emptyText,
  limit = 4,
}: {
  title: string;
  /** Already partitioned and already sorted soonest-first by the server. */
  rows: DueRow[];
  /**
   * The count the pill shows. Passed in rather than read off `rows.length` so a card
   * showing four of eleven says ELEVEN — ⭐ a capped list that prints the cap as its total
   * is the shape that makes a backlog look handled.
   */
  total: number;
  viewAllTab: 'maintenance' | 'reminders';
  emptyText: string;
  limit?: number;
}) {
  const shown = rows.slice(0, limit);
  const hidden = total - shown.length;

  return (
    /*
     * ⭐ `min-w-0` is load-bearing, not tidiness. A grid item's default `min-width` is
     * `auto`, which means "never shrink below your content" — so one long asset name
     * ("Unit 12 — Transit 250 (Lewis Center yard)") pushed this card to 472px inside a
     * 390px viewport and the whole page scrolled sideways. The `truncate` further down
     * could do nothing about it: truncation needs an ancestor that is allowed to be
     * narrower than its text, and until this class was here, none of them were.
     *
     * MEASURED in a browser at 390px, not reasoned about — no node test in this repo can
     * see a layout overflow, which is why `vitest.config.ts` says rendering is proved in
     * a real browser.
     */
    <section className="min-w-0 rounded-[var(--radius)] border border-border bg-surface">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-sm font-semibold">{title}</h2>
          {total > 0 ? <StatusBadge tone="warn">{total}</StatusBadge> : null}
        </div>
        {/*
         * The address `pageTabs.ts` reserved for exactly this link when 107 shipped the
         * register: `?tab=maintenance` / `?tab=reminders`. It is a real deep link, so the
         * whole list opens on the right tab rather than on the register with the reader
         * left to find it.
         */}
        <Link
          to="/fleet/assets"
          search={{ tab: viewAllTab }}
          className="text-sm text-[var(--sw-brand-text)] underline decoration-dotted"
        >
          View all
        </Link>
      </header>

      {shown.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul>
          {shown.map((r) => {
            const chip = dueChip(r);
            return (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <Link
                    to="/fleet/assets/$assetId"
                    params={{ assetId: r.assetId }}
                    className="block truncate font-medium underline decoration-dotted"
                  >
                    {r.assetName}
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">{dueTitle(r)}</p>
                </div>
                <div className="shrink-0">
                  <StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/*
       * ⭐ The list is capped; the card says so. Without this line four rows out of eleven
       * read as "four things are due", and the seven nobody mentioned are the ones that
       * lapse. The count in the pill is the true total and this sentence reconciles it
       * with what is on screen.
       */}
      {hidden > 0 ? (
        <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {hidden === 1 ? '1 more not shown' : `${hidden} more not shown`}
        </p>
      ) : null}
    </section>
  );
}
